import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTheme } from "@mui/material/styles"
// Hooks and Redux
import { useAppSelector } from "../../../redux/hooks"
import { ThemeProvider } from "@mui/material/styles"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import FormControl from "@mui/material/FormControl"
import CircularProgress from "@mui/material/CircularProgress"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Dialog from "@mui/material/Dialog"
import { useTranslation } from "react-i18next"
import jwtDecode from "jwt-decode"
// Styles
import { green } from "@mui/material/colors"
import theme from "../../../styles/theme"
import Swal from "sweetalert2"
// Services
import {updateStatusAllECData} from "../../../services/ExcelECService"

import Autocomplete, {
  AutocompleteRenderOptionState,
} from "@mui/material/Autocomplete"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"

// Types
import { TokenPayload } from "../../../@types"
import {  IStatusOption,IHighlightPart,IUpdateStatusPayload, InputDataToECProps} from "../../../@types/ECData.type"


const statusList: IStatusOption[] = [
  { id: "active", status: "Active", value: true },
  { id: "inactive", status: "Inactive", value: false },
]

export default function PopupChangeStatusEC(props: InputDataToECProps) {
  const { typeEC, data, onClose, open, ...other } = props
  const { t } = useTranslation()
  const themeUI = useTheme()
  const token = localStorage.getItem("access_token") || ""
  const decoded: TokenPayload = jwtDecode(token)

  const [submittedLoading, setSubmittedLoading] = useState<boolean>(false)
  const [selectedStatus, setSelectedStatus] = useState<boolean>(false)

  useEffect(() => {
      if(data && data?.id){
          setSelectedStatus(data?.is_active ? true : false)
      }

  }, [open ,data])

  const onSubmitted = async () => {
    try {
      setSubmittedLoading(true)
      const reqData: IUpdateStatusPayload = {
        id: data?.id,
        is_active: selectedStatus,
        modified_by: decoded.Username,
        typeEC:typeEC
      }
      const respData = await updateStatusAllECData(reqData) 

      if (respData?.status === 200 && respData?.data?.success === true) {
        setSubmittedLoading(false)
        onClose(true)
        Swal.fire({
          icon: "success",
          title: `${t("Success")}`,
          text: `${t("Update EC data successfully")}`,
          confirmButtonText: `${t("ok")}`,
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.reload()
          }
        })
      } else {
        setSubmittedLoading(false)
        onClose(true)
        Swal.fire({
          icon: "error",
          title: `${t("Error")} [${
            respData?.status
          }]`,
          text: `${t("text_can_not_edit_status")} [${
            respData?.data?.message
          }]`,
          confirmButtonText: `${t("ok")}`,
        })
      }
    } catch (error) {
      console.log(error)
      setSubmittedLoading(false)
      onClose(true)
      Swal.fire({
        icon: "error",
        title: `${t("Error")}`,
        text: `${t("Please try again")}`,
        confirmButtonText: `${t("ok")}`,
      })
    }
  }


 

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        sx={{
          "& .MuiDialog-paper": {
            width: "80%",
            height: "60%",
            position: "relative",
            padding: themeUI.spacing(2),
            [themeUI.breakpoints.up("md")]: {
              width: "40%",
              height: "50%",
            },
            [themeUI.breakpoints.up("lg")]: {
              width: "30%",
              height: "60%",
            },
          },
        }}
        open={open}
        {...other}
      >
        <DialogTitle
          id="scroll-dialog-title"
          sx={{ fontWeight: "bold", fontSize: { xs: 16, sm: 18 } }}
        >
          {t("title_status_active")}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <Box sx={{ marginBottom: 2 }}>
                <Autocomplete
                  disableClearable
                  onChange={(event, newValue: IStatusOption | null) => {
                    setSelectedStatus(newValue?.value ?? false)
                  }}
                  options={statusList}
                  getOptionLabel={(option: IStatusOption) => option.status}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  value={
                    statusList.find((item) => item.value === selectedStatus)
                  }
                  renderOption={(
                    liProps,
                    option: IStatusOption,
                    { inputValue }: AutocompleteRenderOptionState
                  ) => {
                    const matches = match(option.status, inputValue, {
                      insideWords: true,
                    })
                    const parts: IHighlightPart[] = parse(
                      option.status,
                      matches
                    )
                    return (
                      <li {...liProps}>
                        <div>
                          {parts.map((part, index) => (
                            <span
                              key={index}
                              style={{
                                fontWeight: part.highlight ? 700 : 400,
                              }}
                            >
                              {part.text}
                            </span>
                          ))}
                        </div>
                      </li>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("title_status_active") + " *"}
                      label={t("title_status_active") + " *"}
                    />
                  )}
                />
              </Box>
            </FormControl>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                onClose()
                setSubmittedLoading(false)
              }}
              sx={{ px: 3 }}
            >
              {t("Cancel")}
            </Button>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmitted}
            sx={{ px: 3 }}
            disabled={submittedLoading}
          >
            {t("Confirm")}
            {submittedLoading && (
              <CircularProgress
                size={24}
                sx={{
                  color: green[500],
                  position: "absolute",
                  zIndex: 999,
                }}
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}